import React, { useContext } from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import { Helmet } from "react-helmet"
import { Provider as AlertProvider } from "react-alert"
import { ThemeProvider } from "styled-components"
import { useLocation } from "@reach/router"

import { defaultTheme } from "../../lib/styled-system/constants"
import { mergeThemes } from "../../lib/styled-system/helpers"

import { PopupsContext } from "../../contexts/PopupsContext"

import GlobalStyle from "../utils/GlobalStyle"
import { normalize } from "../../utils/utils"

import Alert from "../atoms/Alert"
import CookiePopup from "../page/CookiePopup"
import HtmlReactParser from "html-react-parser"
import Footers from "../page/Footers"
import Headers from "../page/Headers"
import PopupSections from "../page/Popups"

// can't import in RichText.js since it's a Node module
// https://github.com/vercel/next.js/issues/19936
// this import supports nested lists in rich text
import "draft-js/dist/Draft.css"

const Layout = ({
  header,
  footer,
  site,
  firstSectionStyling,
  lastSectionStyling,
  footerStyle,
  children,
  pageSpecificNavTheme,
  isTransparentNav,
  bgOpacityNav,
  htmlSnippets,
}) => {
  const location = useLocation()
  const isHomepage = location.pathname === "/"
  const normalizedSite = normalize(site) //normalize site to remove data and attributes keys added by strapi v4
  const theme = normalizedSite?.brand?.theme
  const completeTheme = mergeThemes(defaultTheme, theme)

  const renderSnippets = {
    top: [],
    bottom: [],
  }
  htmlSnippets?.map(snippet => {
    renderSnippets[snippet.dataObj.htmlDetails.position].push(
      HtmlReactParser(snippet.dataObj.html.replace(/\n/g, ""))
    )
  })

  const {
    popupSectionId: popupId,
    openModalSectionById,
    isModalOpen,
    setModal,
  } = useContext(PopupsContext)

  const popupSectionProps = {
    popupId,
    onClose: () => {
      openModalSectionById(null)
      setModal(false)
    },
    isModalOpen,
    setModal,
  }

  return (
    <>
      {renderSnippets.top}
      <Helmet>
        <noscript>
          {/* If there's no Javascript, show all react-reveal elements */}
          {`<style>.react-reveal {visibility: visible !important;}</style>`}
        </noscript>
      </Helmet>
      <ThemeProvider theme={completeTheme}>
        <AlertProvider
          template={Alert}
          timeout={4000}
          transition="fade"
          containerStyle={{ marginTop: "32px", zIndex: 200 }}
        >
          <GlobalStyle />
          <Headers
            header={header}
            site={normalizedSite}
            pageSpecificNavTheme={pageSpecificNavTheme}
            isTransparentNav={isTransparentNav}
            bgOpacityNav={bgOpacityNav}
            firstSectionStyling={firstSectionStyling}
            isHomepage={isHomepage}
          />
          <CookiePopup site={normalizedSite} />
          <PopupSections
            {...popupSectionProps}
            site={normalizedSite}
            isHomepage={isHomepage}
          />
          <main>{children}</main>
          <Footers
            footer={footer}
            lastSectionStyling={lastSectionStyling}
            site={normalizedSite}
            isHomepage={isHomepage}
            {...footerStyle}
          />
          {renderSnippets.bottom}
        </AlertProvider>
      </ThemeProvider>
    </>
  )
}

Layout.propTypes = {
  site: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
}

export default Layout

export const query = graphql`
  fragment Site on STRAPI_SITE {
    title
    socialShareImage {
      url
    }
    favicon {
      url
    }
    siteDescription
    siteTitleLocationInPageTitle
    showSiteTitleInPageTitle
    brand {
      theme
    }
    header {
      ...Headers
    }
    footer {
      ...Footers
    }
    cookiePopup {
      ...CookiePopup
    }
    popups {
      ...Popups
    }
  }
`
